import { useMemo } from 'react';

import type { AssetResponse } from '../../api';
import { ASSET_TYPE_KEYS, ASSET_TYPES, type AssetType } from '../../constants/assetTypes';

export const UNASSIGNED_NAMEPLATE_KEY = '__unassigned__';
export const UNASSIGNED_NAMEPLATE_LABEL = 'Unassigned Nameplate';

/** One kind of asset within a nameplate: source images, master images, filmic or OLA. */
export interface AssetTypeGroup {
  assetType: AssetType;
  label: string;
  assets: AssetResponse[];
}

export interface NameplateGroup {
  nameplate: string;
  displayName: string;
  totalCount: number;
  assetTypeCounts: Partial<Record<AssetType, number>>;
  assetTypes: AssetTypeGroup[];
}

export interface HierarchicalGroupingResult {
  nameplateGroups: NameplateGroup[];
  totalAssetCount: number;
  totalNameplateCount: number;
  orderedIds: string[];
  orderedAssets: AssetResponse[];
}

/**
 * Pure function to group assets in a 2-tier hierarchy:
 * Nameplate (Top Level) -> Kind (Second Level) -> Asset (Grid items)
 *
 * Sorting rules:
 * 1. Named nameplates sorted alphabetically (case-insensitive); unassigned pinned at the end.
 *    `pin.nameplate` (the picked brief row's), when given, goes first: every
 *    asset that can serve the row is filed under it, and with hundreds of
 *    assets the user should not have to scroll to find them.
 * 2. Kinds in `ASSET_TYPE_KEYS` order: source images first, then master images, filmic, OLA.
 *    Every asset has a kind, so there is no "unassigned" bucket. In the pinned
 *    section, kinds holding any of `pin.matchingIds` come first (still in that
 *    order among themselves): otherwise a row served by master images would
 *    sit below every source image of its nameplate.
 * 3. orderedIds and orderedAssets flatten the hierarchical traversal for linear selection.
 *
 * An asset carries a *list* of nameplates and is filed under every one of
 * them, so whoever browses either section finds it. Two consequences:
 *  - a section's `totalCount` / `assetTypeCounts` count the asset once per
 *    section it is in, so the section counts can sum above `totalAssetCount`,
 *    which stays the distinct number of assets on the page;
 *  - `orderedIds` / `orderedAssets` list the asset once, where it first
 *    appears, so overlay stepping and URL prefetch never revisit it.
 *
 * Within a kind, assets keep the order they were passed in.
 */
export interface HierarchyPin {
  /** The picked brief row's nameplate, matched case-insensitively. */
  nameplate: string;
  /** The assets that can serve the row. */
  matchingIds: Set<string>;
}

export function groupAssetsByHierarchy(
  assets: AssetResponse[],
  pin?: HierarchyPin | null,
): HierarchicalGroupingResult {
  if (!assets || assets.length === 0) {
    return {
      nameplateGroups: [],
      totalAssetCount: 0,
      totalNameplateCount: 0,
      orderedIds: [],
      orderedAssets: [],
    };
  }

  // 1. Group assets by nameplate key
  const assetsByNameplate = new Map<string, AssetResponse[]>();

  for (const asset of assets) {
    // De-duplicated so ['Puma', 'Puma'] files the asset once under Puma; a
    // list of blanks is the same as no list at all.
    const names = Array.from(
      new Set((asset.nameplate ?? []).map((name) => name.trim()).filter(Boolean)),
    );
    const keys = names.length > 0 ? names : [UNASSIGNED_NAMEPLATE_KEY];
    for (const key of keys) {
      const list = assetsByNameplate.get(key);
      if (list) {
        list.push(asset);
      } else {
        assetsByNameplate.set(key, [asset]);
      }
    }
  }

  // 2. Sort nameplate keys (pinned first, alphabetical, unassigned at the end).
  // Case-insensitive, like the brief-row match the pin stands in for.
  const pinned = pin?.nameplate.trim().toLowerCase() || null;
  const isPinned = (key: string) => pinned !== null && key.toLowerCase() === pinned;
  const sortedNameplateKeys = Array.from(assetsByNameplate.keys()).sort((a, b) => {
    if (isPinned(a) !== isPinned(b)) return isPinned(a) ? -1 : 1;
    if (a === UNASSIGNED_NAMEPLATE_KEY) return 1;
    if (b === UNASSIGNED_NAMEPLATE_KEY) return -1;
    return a.localeCompare(b, undefined, { sensitivity: 'base' });
  });

  const nameplateGroups: NameplateGroup[] = [];
  const orderedAssets: AssetResponse[] = [];
  const orderedIds: string[] = [];
  // A multi-nameplate asset sits in several sections but gets one slot in
  // the ordered lists, at its first appearance.
  const seen = new Set<string>();

  for (const nameplateKey of sortedNameplateKeys) {
    const groupAssets = assetsByNameplate.get(nameplateKey) ?? [];
    const isUnassigned = nameplateKey === UNASSIGNED_NAMEPLATE_KEY;
    const displayName = isUnassigned ? UNASSIGNED_NAMEPLATE_LABEL : nameplateKey;

    // Group assets within this nameplate by kind, in registry order.
    const assetsByType = new Map<AssetType, AssetResponse[]>();
    const assetTypeCounts: Partial<Record<AssetType, number>> = {};

    for (const asset of groupAssets) {
      const assetType = asset.asset_type;
      const list = assetsByType.get(assetType);
      if (list) {
        list.push(asset);
      } else {
        assetsByType.set(assetType, [asset]);
      }
      assetTypeCounts[assetType] = (assetTypeCounts[assetType] ?? 0) + 1;
    }

    const assetTypes: AssetTypeGroup[] = [];

    const hasMatch = (assetType: AssetType) =>
      isPinned(nameplateKey) &&
      (assetsByType.get(assetType) ?? []).some((asset) => pin!.matchingIds.has(asset.id));
    const kindOrder = [
      ...ASSET_TYPE_KEYS.filter(hasMatch),
      ...ASSET_TYPE_KEYS.filter((assetType) => !hasMatch(assetType)),
    ];

    for (const assetType of kindOrder) {
      const typeAssets = assetsByType.get(assetType);
      if (!typeAssets) continue;

      assetTypes.push({
        assetType,
        label: ASSET_TYPES[assetType].label,
        assets: typeAssets,
      });

      for (const asset of typeAssets) {
        if (seen.has(asset.id)) continue;
        seen.add(asset.id);
        orderedAssets.push(asset);
        orderedIds.push(asset.id);
      }
    }

    nameplateGroups.push({
      nameplate: nameplateKey,
      displayName,
      totalCount: groupAssets.length,
      assetTypeCounts,
      assetTypes,
    });
  }

  return {
    nameplateGroups,
    totalAssetCount: assets.length,
    totalNameplateCount: nameplateGroups.length,
    orderedIds,
    orderedAssets,
  };
}

/**
 * Hook to memoize hierarchical asset grouping.
 */
export function useHierarchicalAssets(
  assets: AssetResponse[],
  pin?: HierarchyPin | null,
): HierarchicalGroupingResult {
  return useMemo(() => groupAssetsByHierarchy(assets, pin), [assets, pin]);
}

/**
 * Identifies an asset-type group within a specific nameplate for collapse tracking.
 * The null byte can't occur in either part, so it can't collide the way a
 * plain string join could (nameplate "Puma-master" + "image" vs nameplate "Puma" + "master_image").
 */
export function typeCollapseKey(nameplate: string, assetType: string): string {
  return `${nameplate}\u0000${assetType}`;
}

/**
 * Flattens the hierarchy to just the nameplate/type groups that are not collapsed.
 *
 * `orderedIds`/`orderedAssets` on the grouping result always include every
 * asset, collapsed or not — that's the right shape for prefetching signed
 * URLs and for overlay prev/next stepping. Selection is different:
 * `useAssetSelection` resolves shift-ranges and "select all" against
 * whatever list it's given, so that list has to match what's actually on
 * screen, or a shift-click can silently reach into a collapsed group.
 *
 * A multi-nameplate asset is listed once, at its first visible copy, so
 * "select all" counts it once. A shift-range anchored on a later copy
 * therefore resolves to the first copy's position — acceptable, because a
 * range is a set of ids either way and the asset is selected in every
 * section at once.
 */
export function getVisibleOrdered(
  nameplateGroups: NameplateGroup[],
  collapsedNameplates: Set<string>,
  collapsedTypes: Set<string>,
): { orderedIds: string[]; orderedAssets: AssetResponse[] } {
  const orderedIds: string[] = [];
  const orderedAssets: AssetResponse[] = [];
  const seen = new Set<string>();

  for (const group of nameplateGroups) {
    if (collapsedNameplates.has(group.nameplate)) continue;
    for (const typeGroup of group.assetTypes) {
      if (collapsedTypes.has(typeCollapseKey(group.nameplate, typeGroup.assetType))) continue;
      for (const asset of typeGroup.assets) {
        if (seen.has(asset.id)) continue;
        seen.add(asset.id);
        orderedIds.push(asset.id);
        orderedAssets.push(asset);
      }
    }
  }

  return { orderedIds, orderedAssets };
}
